/**
 * Checklist RENDER model and static tab definitions.
 *
 * The catalog (sections, tasks, English texts) is no longer hardcoded here — the canon lives in
 * llm-common and arrives via GET /geo-checklist/catalog (`useGetGeoChecklistCatalog`).
 * `buildChecklistSections` projects it onto this render model: i18n keys derived from the stable
 * ids, with the catalog's English text as `defaultValue` fallback, so a catalog-only content change
 * ships without a frontend release (English until translations catch up). What stays hardcoded is
 * frontend-only: tab definitions (slugs, layout variant, intro copy keys) and the key takeaways.
 */
import type { GeoChecklistCatalogSection } from '@brand24-original/llm-types';

export interface ChecklistTask {
  id: string;
  titleKey: string;
  defaultTitle: string;
  descriptionKey?: string;
  defaultDescription?: string;
  /** English instruction from the catalog; its presence decides whether the details view opens. */
  defaultInstruction?: string;
  examples?: Array<{ key: string; fallback: string }>;
  instructionChart?: string;
  /** Relative impact, shown as a coloured tag in the playbook view. */
  impact?: 'high' | 'medium' | 'low';
}

export interface ChecklistSection {
  id: string;
  titleKey: string;
  defaultTitle: string;
  subtitleKey?: string;
  defaultSubtitle?: string;
  icon?: string;
  tasks: ChecklistTask[];
}

export interface KeyTakeaway {
  titleKey: string;
  descriptionKey: string;
  sectionId?: string;
}

export interface ChecklistTab {
  type: 'TOPICS_PROMPTS' | 'AI_VISIBILITY';
  labelKey: string;
  slug: string;
  hidden?: boolean;
  introTitleKey: string;
  introSubtitleKey: string;
  introDescKey: string;
  introHelperKey: string;
  introBulletKeys: string[];
  introFooterKey: string;
  introKeyTakeaways?: KeyTakeaway[];
  introFooter2Key?: string;
  showChart?: boolean;
  /** Page layout variant. 'playbook' renders the redesigned GEO Recommendations view. */
  design?: 'classic' | 'playbook';
  sections: ChecklistSection[];
}

/**
 * A tab before its sections arrive from the catalog. `sectionOrder` replaces the old duplicated
 * section arrays: the playbook keeps the catalog's curated order, the classic tab sorts av_1..av_6.
 */
export type ChecklistTabDef = Omit<ChecklistTab, 'sections'> & { sectionOrder: 'catalog' | 'byId' };

const AI_VISIBILITY_TAKEAWAYS: KeyTakeaway[] = [
  { titleKey: 'geoChecklist.av.kt1.title', descriptionKey: 'geoChecklist.av.kt1.desc', sectionId: 'av_1' },
  { titleKey: 'geoChecklist.av.kt2.title', descriptionKey: 'geoChecklist.av.kt2.desc', sectionId: 'av_3' },
  { titleKey: 'geoChecklist.av.kt3.title', descriptionKey: 'geoChecklist.av.kt3.desc', sectionId: 'av_1' },
  { titleKey: 'geoChecklist.av.kt4.title', descriptionKey: 'geoChecklist.av.kt4.desc', sectionId: 'av_4' },
  { titleKey: 'geoChecklist.av.kt5.title', descriptionKey: 'geoChecklist.av.kt5.desc', sectionId: 'av_2' },
  { titleKey: 'geoChecklist.av.kt6.title', descriptionKey: 'geoChecklist.av.kt6.desc', sectionId: 'av_3' },
  { titleKey: 'geoChecklist.av.kt7.title', descriptionKey: 'geoChecklist.av.kt7.desc', sectionId: 'av_4' },
  { titleKey: 'geoChecklist.av.kt8.title', descriptionKey: 'geoChecklist.av.kt8.desc', sectionId: 'av_2' },
  { titleKey: 'geoChecklist.av.kt9.title', descriptionKey: 'geoChecklist.av.kt9.desc', sectionId: 'av_1' },
  { titleKey: 'geoChecklist.av.kt10.title', descriptionKey: 'geoChecklist.av.kt10.desc', sectionId: 'av_3' },
  { titleKey: 'geoChecklist.av.kt11.title', descriptionKey: 'geoChecklist.av.kt11.desc', sectionId: 'av_4' },
  { titleKey: 'geoChecklist.av.kt12.title', descriptionKey: 'geoChecklist.av.kt12.desc', sectionId: 'av_6' },
  { titleKey: 'geoChecklist.av.kt13.title', descriptionKey: 'geoChecklist.av.kt13.desc', sectionId: 'av_1' },
  { titleKey: 'geoChecklist.av.kt14.title', descriptionKey: 'geoChecklist.av.kt14.desc', sectionId: 'av_3' },
  { titleKey: 'geoChecklist.av.kt15.title', descriptionKey: 'geoChecklist.av.kt15.desc', sectionId: 'av_6' },
  { titleKey: 'geoChecklist.av.kt16.title', descriptionKey: 'geoChecklist.av.kt16.desc', sectionId: 'av_2' },
  { titleKey: 'geoChecklist.av.kt17.title', descriptionKey: 'geoChecklist.av.kt17.desc', sectionId: 'av_5' },
];

/**
 * Section titles keep their historical semantic i18n keys (they predate the id convention);
 * subtitles follow as `<key>Subtitle`. A section the map does not know renders the catalog's
 * English via the `defaultValue` path.
 */
const SECTION_TITLE_KEYS: Record<string, string> = {
  tp_1: 'geoChecklist.section.coreTopics',
  tp_2: 'geoChecklist.section.promptGeneration',
  tp_3: 'geoChecklist.section.systemUpload',
  tp_4: 'geoChecklist.section.bestPractices',
  tp_5: 'geoChecklist.section.evaluationMaintenance',
  av_1: 'geoChecklist.section.accessDiscoverability',
  av_2: 'geoChecklist.section.contentAiUnderstanding',
  av_3: 'geoChecklist.section.brandSignals',
  av_4: 'geoChecklist.section.distributionCommunity',
  av_5: 'geoChecklist.section.monitoringCompetitive',
  av_6: 'geoChecklist.section.ownedAiSurfaces',
};

export const buildChecklistSections = (
  catalogSections: GeoChecklistCatalogSection[],
  sectionOrder: 'catalog' | 'byId'
): ChecklistSection[] => {
  const ordered =
    sectionOrder === 'byId'
      ? [...catalogSections].sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }))
      : catalogSections;

  return ordered.map((section) => {
    const titleKey = SECTION_TITLE_KEYS[section.id] ?? `geoChecklist.section.${section.id}`;
    return {
      id: section.id,
      titleKey,
      defaultTitle: section.title,
      ...(section.subtitle !== undefined
        ? { subtitleKey: `${titleKey}Subtitle`, defaultSubtitle: section.subtitle }
        : {}),
      ...(section.icon !== undefined ? { icon: section.icon } : {}),
      tasks: section.tasks.map((task) => ({
        id: task.id,
        titleKey: `geoChecklist.task.${task.id}.title`,
        defaultTitle: task.title,
        ...(task.description !== undefined
          ? {
              descriptionKey: `geoChecklist.task.${task.id}.description`,
              defaultDescription: task.description,
            }
          : {}),
        ...(task.instruction !== undefined ? { defaultInstruction: task.instruction } : {}),
        ...(task.examples !== undefined
          ? {
              examples: task.examples.map((text, i) => ({
                key: `geoChecklist.task.${task.id}.example${i + 1}`,
                fallback: text,
              })),
            }
          : {}),
        ...(task.instructionChart !== undefined ? { instructionChart: task.instructionChart } : {}),
        ...(task.impact !== undefined ? { impact: task.impact } : {}),
      })),
    };
  });
};

export const CHECKLIST_TAB_DEFS: ChecklistTabDef[] = [
  {
    type: 'TOPICS_PROMPTS',
    labelKey: 'geoChecklist.tabs.topicsPrompts',
    slug: 'topics-prompts',
    hidden: true,
    sectionOrder: 'catalog',
    introTitleKey: 'geoChecklist.tp.introTitle',
    introSubtitleKey: 'geoChecklist.tp.introSubtitle',
    introDescKey: 'geoChecklist.tp.introDesc',
    introHelperKey: 'geoChecklist.tp.introHelper',
    introBulletKeys: [
      'geoChecklist.tp.introBullet1',
      'geoChecklist.tp.introBullet2',
      'geoChecklist.tp.introBullet3',
      'geoChecklist.tp.introBullet4',
    ],
    introFooterKey: 'geoChecklist.tp.introFooter',
  },
  {
    type: 'AI_VISIBILITY',
    labelKey: 'geoChecklist.tabs.aiVisibilityV2',
    slug: 'ai-visibility-v2',
    design: 'playbook',
    sectionOrder: 'catalog',
    introTitleKey: 'geoChecklist.av.introTitle',
    introSubtitleKey: 'geoChecklist.av.introSubtitle',
    introDescKey: 'geoChecklist.av.introDesc',
    introHelperKey: 'geoChecklist.av.introHelper',
    introBulletKeys: [],
    introFooterKey: 'geoChecklist.av.introFooter',
    introFooter2Key: 'geoChecklist.av.introFooter2',
    showChart: true,
    introKeyTakeaways: AI_VISIBILITY_TAKEAWAYS,
  },
  {
    type: 'AI_VISIBILITY',
    labelKey: 'geoChecklist.tabs.aiVisibility',
    slug: 'ai-visibility',
    sectionOrder: 'byId',
    introTitleKey: 'geoChecklist.av.introTitle',
    introSubtitleKey: 'geoChecklist.av.introSubtitle',
    introDescKey: 'geoChecklist.av.introDesc',
    introHelperKey: 'geoChecklist.av.introHelper',
    introBulletKeys: [],
    introFooterKey: 'geoChecklist.av.introFooter',
    introFooter2Key: 'geoChecklist.av.introFooter2',
    showChart: true,
    introKeyTakeaways: AI_VISIBILITY_TAKEAWAYS,
  },
];
